import { useMemo } from 'react'

import { CommandMenuView } from '~/components/command-menu/components/CommandMenuView'
import { useCommandArgs } from '~/components/command-menu/hooks/useCommandArgs'
import { useCommandMenu } from '~/components/command-menu/hooks/useCommandMenu'
import { CommandMenuViewType } from '~/components/command-menu/types'
import { AddressDisplay } from '~/components/component-library/AddressDisplay'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { useUserPasskeys } from '~/hooks/useUserPasskeys'
import { SmartAccountRole } from '~/types'

export const SelectAccount = ({ view }: { view: CommandMenuViewType }) => {
  const { smartAccounts } = useSmartAccounts({ orderedByValue: true })
  const { getViewOptions, getViewCallback } = useCommandMenu()
  const { contextArgs } = useCommandArgs()
  const { passkeyIds: userPasskeyIds } = useUserPasskeys()

  const isOwner = contextArgs.orgMember?.role === 'OWNER'

  // Match canCreateTransaction logic: owners can use any non-root account,
  // non-owners need a passkey on the account
  const signerSmartAccounts = smartAccounts.filter((a) => {
    if (a.role === SmartAccountRole.ROOT) return false
    if (isOwner) return true
    return a.signers.some((s) => userPasskeyIds.has(s.id))
  })

  const viewOptions = getViewOptions()
  const placeholder =
    viewOptions?.placeholder ?? 'Select an account to go to...'

  const accounts = useMemo(
    () =>
      (viewOptions?.signersOnly ? signerSmartAccounts : smartAccounts).filter(
        (smartAccount) => smartAccount.address !== contextArgs.address,
      ),
    [smartAccounts, signerSmartAccounts, contextArgs.address, viewOptions],
  )

  return (
    <CommandMenuView
      items={accounts.map((smartAccount) => ({
        label: () => (
          <AddressDisplay
            address={smartAccount.address}
            layout="standard"
            size="small"
            isInternal
            hidePopover
          />
        ),
        icon: () => undefined,
        value: () => `${smartAccount.name} - ${smartAccount.address}`,
        onSelect: ({ actions: { navigate, setActiveView } }) => {
          const callback = getViewCallback()

          if (callback) {
            callback({ address: smartAccount.address })
          } else {
            // Default behavior: navigate to the account
            navigate(`/accounts/${smartAccount.address}`)
            setActiveView(null)
          }
        },
        shortcuts: [],
      }))}
      placeholder={placeholder}
      view={view}
      primaryActionLabel={viewOptions?.primaryActionLabel ?? 'Open'}
    />
  )
}
